import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Avatar from "@/components/Avatar";
import AvatarSetup from "@/components/AvatarSetup";
import { toast } from "sonner";

const MANUAL_STATUSES = [
  { id: "available", label: "Available" },
  { id: "busy", label: "Busy" },
  { id: "focus", label: "Focus" },
  { id: "away", label: "Away" },
];

/** Popular IANA timezones (short list — no heavy library). */
const TIMEZONES = [
  "UTC", "Europe/London", "Europe/Berlin", "Europe/Paris", "Asia/Jakarta",
  "Asia/Singapore", "Asia/Shanghai", "Asia/Kolkata", "Asia/Tokyo",
  "Australia/Sydney", "Africa/Johannesburg",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo",
];

/**
 * Full-screen profile settings dialog. Users edit only their own profile;
 * offline status is system-controlled (not selectable here).
 */
export default function ProfileSettings({ open, onOpenChange }) {
  const { user, setUser, refresh } = useAuth();
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [form, setForm] = useState(() => ({
    name: user?.name || "",
    title: user?.title || "",
    department: user?.department || "",
    timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    short_status: user?.short_status || "",
    status: MANUAL_STATUSES.find((s) => s.id === user?.status) ? user.status : "available",
  }));

  const save = async () => {
    try {
      const { data } = await api.patch("/me", form);
      setUser?.(data);
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save profile");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" aria-describedby="profile-settings-desc">
          <DialogHeader>
            <DialogTitle>Edit your profile</DialogTitle>
            <DialogDescription id="profile-settings-desc">
              Update your name, role, and how teammates see you across TOVRA.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar user={user} size="xxl" showStatus={false} data-testid="profile-settings-avatar"/>
              <div className="flex-1">
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-zinc-500">{user?.email}</div>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => setAvatarOpen(true)} data-testid="open-avatar-setup-btn">
                  Change avatar
                </Button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase text-zinc-500">Full name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name"/>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase text-zinc-500">Job title</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="profile-title"/>
              </div>
              <div>
                <label className="text-xs uppercase text-zinc-500">Department</label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="profile-department"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase text-zinc-500">Time zone</label>
                <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                  <SelectTrigger data-testid="profile-timezone"><SelectValue/></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase text-zinc-500">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="profile-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {MANUAL_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase text-zinc-500">Status message</label>
              <Input maxLength={80} value={form.short_status} onChange={(e) => setForm({ ...form, short_status: e.target.value })} placeholder="e.g. Heads down until 5pm" data-testid="profile-short-status"/>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="flex-1 bg-[var(--brand-primary)] text-white" onClick={save} data-testid="profile-save">Save changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={avatarOpen} onOpenChange={setAvatarOpen}>
        <DialogContent className="max-w-3xl" aria-describedby="avatar-setup-desc">
          <DialogHeader>
            <DialogTitle>Personal avatar</DialogTitle>
            <DialogDescription id="avatar-setup-desc">
              Upload a photo or design an illustrated avatar. Your selection persists across every workspace view.
            </DialogDescription>
          </DialogHeader>
          <AvatarSetup onSaved={async () => { setAvatarOpen(false); await refresh?.(); }} onCancel={() => setAvatarOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
